/**
 * "Email me a link to set a new password", shared by both Settings surfaces.
 *
 * Extracted from the tenant Settings modal so the owner portal can offer the
 * same flow without a second copy of the wording. The copy is the component:
 * a reset that happens in an email, on another page, and changes nothing here
 * is surprising unless every step is spelled out, and two hand-maintained
 * versions of that explainer would drift apart the first time one is touched.
 *
 * `intent` only swaps the heading:
 *   'primary'     — this is the account's password entry (tenant modal)
 *   'alternative' — it sits under a working change-password form (owner
 *                   Settings), so it has to answer "what if I don't know my
 *                   current password" rather than compete with the form above.
 *
 * Copy stays as inline EN/DE ternaries rather than t() on purpose: a missing
 * translation key blanks the string, and silently dropping the four steps or
 * the expiry warning is worse than shipping them untranslated.
 */
import { useState } from "react";
import { CheckCircle2, Clock, Loader2, Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

export function PasswordResetCard({
  email,
  intent = "primary",
}: {
  /** Address the link goes to. Empty disables the button. */
  email: string;
  intent?: "primary" | "alternative";
}) {
  const { resetPasswordForEmail } = useAuth();
  const { language } = useLanguage();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const de = language === "de";
  const heading =
    intent === "alternative"
      ? de
        ? "Passwort vergessen?"
        : "Forgot your current password?"
      : de
        ? "Passwort zurücksetzen"
        : "Reset password";

  return (
    <Card className="rounded-2xl border border-border/60 shadow-sm">
      <div className="p-5 sm:p-6 space-y-4">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-foreground">
            {heading}
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {de
              ? "Wir senden dir einen Link per E-Mail, mit dem du ein neues Passwort festlegen kannst."
              : "We'll email you a link to choose a new password."}
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 p-4">
          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {de ? "E-Mail" : "Email"}
            </p>
            <p className="text-sm font-medium text-foreground truncate">
              {email || (de ? "Nicht gesetzt" : "Not set")}
            </p>
          </div>
        </div>

        {/* Spell the whole flow out before the user clicks: the reset happens
            in an email, on another page, and nothing changes here — all of
            which is surprising unless stated. */}
        <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-2">
          <p className="text-sm font-medium text-foreground">
            {de ? "So funktioniert es" : "How this works"}
          </p>
          <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal pl-4">
            <li>
              {de
                ? "Du klickst unten auf „Link zum Zurücksetzen senden“ – wir senden sofort eine E-Mail an die oben genannte Adresse."
                : "You click “Send reset link” below — we email the address above right away."}
            </li>
            <li>
              {de
                ? "Öffne die E-Mail und klicke auf den Link darin (schau auch im Spam-Ordner)."
                : "Open that email and click the link inside (check your spam folder too)."}
            </li>
            <li>
              {de
                ? "Der Link öffnet eine coliviq-Seite, auf der du dein neues Passwort zweimal eingibst und speicherst."
                : "The link opens a coliviq page where you type your new password twice and save it."}
            </li>
            <li>
              {de
                ? "Ab dann meldest du dich mit dem neuen Passwort an. Dein altes Passwort gilt bis dahin weiter, und du bleibst hier angemeldet."
                : "From then on you sign in with the new password. Your old one keeps working until then, and you stay signed in here."}
            </li>
          </ol>
          <p className="text-xs text-muted-foreground pt-1">
            {de
              ? "Nichts wird sofort geändert – dein Passwort ändert sich erst, wenn du es über den Link selbst festlegst. Wenn du die E-Mail ignorierst, bleibt alles wie es ist."
              : "Nothing changes immediately — your password only changes once you set it yourself via the link. Ignore the email and everything stays as it is."}
          </p>
        </div>

        {sent ? (
          <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/10 p-4">
            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium text-foreground">
                {de ? "E-Mail gesendet" : "Email sent"}
              </p>
              <p className="text-sm text-muted-foreground">
                {de
                  ? `Wir haben den Link an ${email} gesendet. Bitte prüfe dein Postfach (auch Spam) und klicke auf den Link, um dein neues Passwort festzulegen. Nicht angekommen? Sende ihn unten erneut.`
                  : `We sent the link to ${email}. Check your inbox (and spam) and click it to set your new password. Didn't arrive? Send it again below.`}
              </p>
            </div>
          </div>
        ) : null}

        <Button
          onClick={async () => {
            if (!email) return;
            setSending(true);
            try {
              const { error } = await resetPasswordForEmail(email);
              // resetPasswordForEmail owns the toast for both outcomes; we
              // only mirror success inline so the card shows what happened.
              if (!error) setSent(true);
            } finally {
              setSending(false);
            }
          }}
          disabled={sending || !email}
          className="rounded-xl"
        >
          {sending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {de ? "Senden…" : "Sending…"}
            </>
          ) : (
            <>
              <Lock className="mr-2 h-4 w-4" />
              {sent
                ? de
                  ? "Link erneut senden"
                  : "Resend link"
                : de
                  ? "Link zum Zurücksetzen senden"
                  : "Send reset link"}
            </>
          )}
        </Button>

        {/* Highlighted, not fine print: this is the answer to "my link says it
            expired", so it has to be the thing people actually read. */}
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/35 bg-amber-500/[0.12] px-4 py-3 shadow-sm">
          <Clock className="h-4 w-4 shrink-0 mt-0.5 text-amber-700" />
          <p className="text-sm font-medium text-amber-950/95 leading-relaxed">
            {de
              ? "Der Link ist aus Sicherheitsgründen nur begrenzt gültig. Ist er abgelaufen, sende hier einfach einen neuen."
              : "For security the link is only valid for a limited time. If it expires, just send a new one from here."}
          </p>
        </div>
      </div>
    </Card>
  );
}

export default PasswordResetCard;
